import { fromISODate } from "./calendar";

/**
 * A certificate of service: the page a worker takes to their next employer,
 * and to the UIF.
 *
 * What it states is what a certificate of service is for — who, for whom,
 * doing what, from when to when, and what they were earning at the end. The
 * reason for leaving is on it only when the worker asked for it to be; a
 * certificate that says "dismissed" unasked follows somebody from job to job.
 *
 * Pure HTML, so it can be tested without a phone and printed by expo-print.
 */

export interface CertificateInput {
  employeeName: string;
  employerName: string;
  employerAddress: string;
  work: string;
  startedOn: string;
  endedOn: string;
  /** Already formatted: "R 30.23 per hour, 5 days a week". */
  remuneration: string;
  /** Only when the worker asked for it. */
  reason?: string | null;
  issuedOn: string;
}

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** "14 September 2026" — a certificate is read for years, so the year is always there. */
export function longISODate(iso: string): string {
  const d = fromISODate(iso);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : "";
}

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export function certificateOfServiceHtml(c: CertificateInput): string {
  const row = (label: string, value: string) =>
    value ? `<tr><th>${label}</th><td>${esc(value)}</td></tr>` : "";

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<style>
  @page { size: A4 portrait; margin: 20mm; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Roboto, -apple-system, "Helvetica Neue", Arial, sans-serif; color: #111; font-size: 11.5pt; }
  .page { min-height: 250mm; display: flex; flex-direction: column; }
  h1 { font-size: 24pt; margin: 0 0 2mm; letter-spacing: -0.2pt; }
  .lead { color: #444; margin: 0 0 10mm; }
  p { line-height: 1.6; margin: 0 0 6mm; }
  table { width: 100%; border-collapse: collapse; margin: 2mm 0 10mm; }
  th, td { text-align: left; vertical-align: top; padding: 3mm 0; border-bottom: 0.3mm solid #ddd; }
  th { width: 42%; font-weight: 600; color: #444; }
  .sign { display: flex; gap: 12mm; margin-top: 16mm; }
  .sign div { flex: 1; border-top: 0.3mm solid #111; padding-top: 2mm; font-size: 9.5pt; color: #444; }
  .foot { margin-top: auto; padding-top: 6mm; font-size: 8.5pt; color: #888; }
</style></head>
<body><div class="page">
  <h1>Certificate of Service</h1>
  <p class="lead">Issued ${longISODate(c.issuedOn)}</p>

  <p>This certifies that <b>${esc(c.employeeName)}</b> was employed by <b>${esc(c.employerName)}</b>
  from ${longISODate(c.startedOn)} to ${longISODate(c.endedOn)}.</p>

  <table>
    ${row("Employee", c.employeeName)}
    ${row("Employer", c.employerName)}
    ${row("Employer's address", c.employerAddress)}
    ${row("Work done", c.work)}
    ${row("Employed from", longISODate(c.startedOn))}
    ${row("Employed until", longISODate(c.endedOn))}
    ${row("Remuneration on the last day", c.remuneration)}
    ${c.reason ? row("Reason for leaving", c.reason) : ""}
  </table>

  <div class="sign">
    <div>Signed for the employer</div>
    <div>Name</div>
    <div>Date</div>
  </div>

  <div class="foot">Prepared from the employment record kept in Shifty.</div>
</div></body></html>`;
}
